<template>
    <article class="pcl-card" :class="`is-${tone}`" :style="cardStyle">
        <header class="pcl-head">
            <BrandTileCmp :size="34" :image="markUrl" :icon="isCustom ? 'store-alt' : null"
                :text="brand.monogram" :background="brand.color" :color="brand.textColor" :muted="!channelIsActive" />

            <div class="pcl-head-text">
                <h3 class="pcl-title" :title="storeChannel.channel.name">{{storeChannel.channel.name}}</h3>
                <span class="pcl-status" :class="`is-${status.tone}`" :title="status.hint">
                    <span class="pcl-status-dot"></span>
                    {{status.label}}
                </span>
            </div>
        </header>

        <div class="pcl-link">
            <!-- Fio entre o id gravado no Muven e o produto que o canal devolve para ele -->
            <div class="pcl-wire" aria-hidden="true" v-if="checkInChannel">
                <span class="pcl-node pcl-node-top"></span>
                <span class="pcl-wire-line"></span>
                <span class="pcl-node pcl-node-bottom"></span>
            </div>

            <div class="pcl-link-body">
                <p class="pcl-label">{{idLabel}}</p>

                <div class="pcl-id-row">
                    <code class="pcl-id" v-if="hasId">{{storeChannel.idExternalChannel}}</code>
                    <span class="pcl-id is-empty" v-else>Sem associação</span>

                    <button v-if="associable && hasId" type="button" class="pcl-swap-btn"
                        title="Trocar o produto associado neste canal" @click="associate">
                        <fa icon="edit" /> Trocar
                    </button>
                    <!-- target _blank: o router-link deixa o navegador abrir o href em outra aba -->
                    <router-link v-else-if="associationUrl" :to="associationUrl" class="pcl-swap-btn"
                        target="_blank" rel="noopener"
                        title="Abre em outra aba a tela de associação deste canal, que permite vários anúncios por produto">
                        <fa icon="external-link-alt" /> Gerenciar
                    </router-link>
                </div>

                <!-- Resultado da conferência no canal do id gravado -->
                <div class="pcl-result" aria-live="polite" v-if="checkInChannel">
                    <div v-if="view.state === 'loading'" class="pcl-skeleton">
                        <span></span><span></span>
                    </div>

                    <template v-else-if="view.state === 'found'">
                        <p class="pcl-verdict is-ok">
                            <fa icon="check-circle" />
                            Confere no canal
                        </p>
                        <p class="pcl-product-name">{{view.match.name}}</p>
                        <p class="pcl-product-meta">
                            <span class="pcl-kind" :class="{'is-child': view.match.isVariation}">
                                {{view.match.isVariation ? 'Variação' : 'Produto simples'}}
                            </span>
                            <span v-if="variationLabel">{{variationLabel}}</span>
                            <span v-if="view.match.skuCode">SKU {{view.match.skuCode}}</span>
                        </p>
                        <p class="pcl-product-ids" v-if="view.match.isVariation">
                            <span>{{parentIdLabel}} <code>{{view.match.idParent}}</code></span>
                            <span>{{childIdLabel}} <code>{{view.match.idVariation}}</code></span>
                        </p>
                    </template>

                    <template v-else-if="view.state === 'gridOk'">
                        <p class="pcl-verdict is-ok">
                            <fa icon="check-circle" />
                            Confere no canal
                        </p>
                        <p class="pcl-product-name">{{view.match.name}}</p>
                        <p class="pcl-product-meta" v-if="!gridItems.length">
                            <span class="pcl-kind is-child">Produto com variações</span>
                            <span>{{view.match.variationIds.length}} variações no canal</span>
                        </p>
                        <GridCoverageBarCmp v-else :items="gridItems" />
                        <p class="pcl-hint" v-if="gridCheck.oneByOne && !gridCheck.checking.length">
                            O canal não lista as variações pelo ID do pai, então conferimos cada uma pelo próprio ID.
                        </p>
                        <button v-if="gridCheck.unchecked.length && !gridCheck.checking.length" type="button"
                            class="pcl-link-btn pcl-grid-more" @click="verifyRemaining">
                            <fa icon="sync-alt" />
                            <span>Conferir {{gridCheck.unchecked.length === 1 ? 'a outra variação' : `as outras ${gridCheck.unchecked.length} variações`}}</span>
                        </button>
                    </template>

                    <!-- Pai "simples", mas a grade tem IDs de variação: confere uma a uma antes de dar o veredito -->
                    <template v-else-if="view.state === 'gridChecking'">
                        <p class="pcl-verdict is-muted">
                            <fa icon="sync-alt" spin />
                            Conferindo as variações no canal
                        </p>
                        <p class="pcl-product-name" v-if="view.match.name">{{view.match.name}}</p>
                        <GridCoverageBarCmp :items="gridItems" />
                    </template>

                    <template v-else-if="view.state === 'gridSimple' || view.state === 'gridVariation'">
                        <p class="pcl-verdict" :class="view.state === 'gridSimple' ? 'is-warn' : 'is-bad'">
                            <fa icon="exclamation-triangle" />
                            {{view.state === 'gridSimple' ? 'No canal é um produto sem variações' : 'Este ID é de uma variação'}}
                        </p>
                        <p class="pcl-product-name" v-if="view.match.name">{{view.match.name}}</p>
                        <p class="pcl-hint">
                            O produto pai de uma grade precisa estar associado ao produto pai do canal, que tem as variações.
                        </p>
                        <GridCoverageBarCmp v-if="gridItems.length" :items="gridItems" />
                        <button v-if="associable" type="button" class="pcl-fix-btn" @click="associate">
                            <fa icon="link" /> Corrigir associação
                        </button>
                    </template>

                    <template v-else-if="view.state === 'parent'">
                        <p class="pcl-verdict is-warn">
                            <fa icon="exclamation-triangle" />
                            Este ID é de um produto pai
                        </p>
                        <p class="pcl-product-name">{{view.match.name}}</p>
                        <p class="pcl-hint">
                            Ele tem {{view.match.variations.length}} variações no canal. Um produto simples
                            no Muven deve estar associado a uma delas, senão os pedidos podem não ser encontrados.
                        </p>
                        <button v-if="associable" type="button" class="pcl-fix-btn" @click="associate">
                            <fa icon="link" /> Escolher a variação certa
                        </button>
                    </template>

                    <template v-else-if="view.state === 'notFound'">
                        <p class="pcl-verdict is-bad">
                            <fa icon="exclamation-triangle" />
                            Não encontrado no canal
                        </p>
                        <p class="pcl-hint">
                            O canal não reconhece o ID <code>{{storeChannel.idExternalChannel}}</code>. Se pedidos
                            com este produto estão dando erro, a associação provavelmente está errada.
                        </p>
                        <button v-if="associable" type="button" class="pcl-fix-btn" @click="associate">
                            <fa icon="link" /> Corrigir associação
                        </button>
                    </template>

                    <template v-else-if="view.state === 'error'">
                        <p class="pcl-verdict is-muted">
                            <fa icon="info-circle" />
                            Não deu para consultar o canal agora
                        </p>
                        <p class="pcl-hint" :title="view.message">{{errorHint}}</p>
                        <button type="button" class="pcl-link-btn" @click="recheck">
                            <fa icon="sync-alt" /> Tentar de novo
                        </button>
                    </template>

                    <template v-else-if="view.state === 'manual'">
                        <p class="pcl-hint" v-if="!channelIsActive">O canal está desativado, então a conferência é manual.</p>
                        <p class="pcl-hint" v-else>Este canal limita as consultas, então a conferência é manual.</p>
                        <button type="button" class="pcl-link-btn" @click="recheck">
                            <fa icon="sync-alt" /> Conferir no canal
                        </button>
                    </template>

                    <template v-else-if="view.state === 'unsupported'">
                        <p class="pcl-hint" v-if="searchProfile.supportsSearch">
                            Este canal não permite conferir o ID por aqui.
                            <template v-if="associable">Para trocar, use o botão Trocar.</template>
                        </p>
                        <p class="pcl-hint" v-else>A conferência e a associação por aqui não estão disponíveis para este canal.</p>
                    </template>

                    <template v-else-if="view.state === 'empty'">
                        <p class="pcl-hint">
                            Este produto ainda não está associado a nenhum produto deste canal.
                        </p>
                        <button v-if="associable" type="button" class="pcl-fix-btn" @click="associate">
                            <fa icon="link" /> Associar produto
                        </button>
                    </template>
                </div>
            </div>
        </div>

        <dl class="pcl-figures">
            <div>
                <dt>Listagem</dt>
                <dd>{{storeChannel.listPrice | currency}}</dd>
            </div>
            <div>
                <dt>Venda</dt>
                <dd>{{storeChannel.sellPrice | currency}}</dd>
            </div>
            <div v-if="showStock">
                <dt>Estoque</dt>
                <dd>{{storeChannel.calculatedStockQty | infoOrPlaceholder}}</dd>
            </div>
        </dl>
    </article>
</template>

<script>
import { ChannelEnum } from '@c2b/muven-commons'
import productService from '@/product/services/ProductService'
import { getChannelBrand, getChannelGlow } from '@/channel/models/ChannelBrand'
import BrandTileCmp from '@/utils/components/BrandTileCmp.vue'
import { infoOrPlaceholder } from '@/utils/filters/InformationOrPlaceholderFilter'
import {
    ChannelProductMatchStatus,
    getChannelSearchProfile,
    isNotFoundError,
    resolveGridParent,
    resolveChannelProduct,
} from '@/channel/utils/ChannelProductAssociationHelper'
import {
    channelLookupKey,
    channelLookupQueue,
    isLookupCancelled,
    isLookupTimeout,
} from '@/channel/utils/ChannelLookupQueue'
import GridCoverageBarCmp from './GridCoverageBarCmp.vue'

// Conferência automática de variações pelo próprio id, por card (o resto fica a pedido)
const GRID_AUTO_CHECK = 12

function emptyGridCheck() {
    return {found: [], missing: [], failed: [], checking: [], unchecked: [], oneByOne: false}
}

const STATE_BY_MATCH = {
    [ChannelProductMatchStatus.Found]: 'found',
    [ChannelProductMatchStatus.ParentWithVariations]: 'parent',
    [ChannelProductMatchStatus.NotFound]: 'notFound',
}

export default {
    name: 'ProductChannelLinkCardCmp',
    components: {
        BrandTileCmp,
        GridCoverageBarCmp,
    },
    props: {
        storeChannel: {
            type: Object,
            required: true,
        },
        idStore: {
            type: [Number, String],
            default: null,
        },
        idMuven: {
            type: [Number, String],
            default: null,
        },
        // false: só mostra o ID gravado, sem consultar o canal nem permitir edição
        checkInChannel: {
            type: Boolean,
            default: true,
        },
        idLabel: {
            type: String,
            default: 'ID no canal',
        },
        showStock: {
            type: Boolean,
            default: true,
        },
        // Canal desativado nas configurações: nada é sincronizado, mesmo com o produto ativo nele
        channelIsActive: {
            type: Boolean,
            default: true,
        },
        // Grade: o id gravado é o do produto pai e deve apontar para um produto com variações
        gradeMode: {
            type: Boolean,
            default: false,
        },
        // Grade: variações do Muven com o id gravado hoje neste canal e nesta loja ({idMuven, label, currentId})
        gridVariations: {
            type: Array,
            default: () => [],
        },
        // Mostra os botões que abrem a associação guiada (evento `associate`)
        associable: {
            type: Boolean,
            default: false,
        },
        // Canal com tela de associação própria (ex.: Mercado Livre com vários anúncios)
        associationUrl: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            // Conferência do id gravado
            current: {state: 'idle', match: null, message: ''},
            // Grade: ids de variação que o canal reconheceu, os que não reconheceu e os ainda em conferência
            gridCheck: emptyGridCheck(),
        }
    },
    filters: {
        infoOrPlaceholder,
    },
    computed: {
        brand() {
            return getChannelBrand(this.storeChannel.channel.enumChannel)
        },
        markUrl() {
            return this.brand.mark ? require(`@/channel/images/marks/${this.brand.mark}`) : null
        },
        isCustom() {
            return this.storeChannel.channel.enumChannel === ChannelEnum.Custom
        },
        cardStyle() {
            const glow = getChannelGlow(this.brand)

            return glow ? {'--pcl-glow': glow} : {}
        },
        status() {
            if (!this.channelIsActive) {
                return {
                    tone: 'off',
                    label: 'Canal inativo',
                    hint: 'O canal está desativado nas configurações, então este produto não é sincronizado com ele.',
                }
            }

            if (this.storeChannel.isActive) {
                return {tone: 'active', label: 'Ativo no canal', hint: 'O produto está ativo neste canal.'}
            }

            return {tone: 'inactive', label: 'Inativo no canal', hint: 'O produto está inativo neste canal.'}
        },
        channelId() {
            return this.storeChannel.idChannel || this.storeChannel.channel.id
        },
        searchProfile() {
            return getChannelSearchProfile(this.storeChannel.channel.enumChannel)
        },
        hasId() {
            return !!this.storeChannel.idExternalChannel
        },
        isShopee() {
            return this.storeChannel.channel.enumChannel === ChannelEnum.Shopee
        },
        parentIdLabel() {
            return this.isShopee ? 'Anúncio' : 'Pai'
        },
        childIdLabel() {
            return this.isShopee ? 'Variação' : 'Filho'
        },
        view() {
            return this.current
        },
        // Uma peça da régua por variação do Muven
        gridItems() {
            if (!this.gradeMode) {
                return []
            }

            const {found, missing, failed, checking} = this.gridCheck

            return this.gridVariations.map((v) => {
                const id = v.currentId ? String(v.currentId) : ''
                let status = 'unknown'

                if (!id) {
                    status = 'empty'
                } else if (checking.includes(id)) {
                    status = 'checking'
                } else if (found.includes(id)) {
                    status = 'ok'
                } else if (missing.includes(id)) {
                    status = 'bad'
                } else if (failed.includes(id)) {
                    status = 'unknown'
                }

                return {...v, currentId: id, status}
            })
        },
        gridSignature() {
            return this.gridVariations.map((v) => `${v.idMuven}:${v.currentId || ''}`).join('|')
        },
        tone() {
            const state = this.view.state

            // A grade só "confere" se as variações conferem; com alguma faltando, fica em alerta
            if (state === 'gridOk' && this.gridItems.some((i) => i.status === 'bad')) {
                return 'warn'
            }

            if (state === 'found' || state === 'gridOk') {
                return 'ok'
            }

            if (state === 'parent' || state === 'gridSimple') {
                return 'warn'
            }

            if (state === 'notFound' || state === 'gridVariation') {
                return 'bad'
            }

            return 'neutral'
        },
        errorHint() {
            if (this.view.timedOut) {
                return 'O canal demorou demais para responder. Tente de novo em instantes.'
            }

            return 'O canal ou o agente de integração não respondeu. Tente de novo em instantes.'
        },
        variationLabel() {
            const attrs = (this.view.match && this.view.match.attributes) || []

            return attrs.map((a) => `${a.name}: ${a.value}`).join(' · ')
        },
    },
    watch: {
        // Id trocado (associação nova): confere de novo sem usar o resultado guardado
        'storeChannel.idExternalChannel'() {
            if (this.checkInChannel) {
                this.checkCurrent({force: true})
            }
        },
        // Mesmo pai, variações reassociadas: só a régua precisa ser refeita
        gridSignature() {
            const parent = this.parentResult

            if (parent && parent.parentId === this.storeChannel.idExternalChannel && ['gridOk', 'gridSimple', 'gridChecking'].includes(this.current.state)) {
                this.verifyGrid(parent, parent.parentId)
            }
        },
    },
    mounted() {
        if (!this.checkInChannel) {
            return
        }

        if (!this.hasId) {
            this.current = {state: 'empty', match: null, message: ''}
            return
        }

        // Mesma regra da tela de associação: canal com limite de requisições não
        // é consultado sozinho, só quando o usuário pede. Canal desativado também
        // não: o agente dele pode nem estar respondendo.
        if (!this.searchProfile.supportsGetById) {
            this.current = {state: 'unsupported', match: null, message: ''}
            return
        }

        if (!this.channelIsActive || (this.storeChannel.channel.hasApiLimitReq && this.storeChannel.channel.hasApiLimitReq())) {
            this.current = {state: 'manual', match: null, message: ''}
            return
        }

        // Só consulta quando o card chega perto da tela: quem não rola até os
        // canais não gera consulta nenhuma
        this.current = {state: 'loading', match: null, message: ''}
        this.whenVisible(() => this.checkCurrent())
    },
    beforeDestroy() {
        this.destroyed = true
        this.stopObserving()
    },
    methods: {
        whenVisible(callback) {
            if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
                callback()
                return
            }

            this.observer = new IntersectionObserver((entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    this.stopObserving()
                    callback()
                }
            }, {rootMargin: '200px 0px'})

            this.observer.observe(this.$el)
        },
        stopObserving() {
            if (this.observer) {
                this.observer.disconnect()
                this.observer = null
            }
        },
        recheck() {
            this.checkCurrent({force: true})
        },
        associate() {
            this.$emit('associate', {state: this.current.state, match: this.current.match})
        },
        async lookup(id, force) {
            try {
                const idStore = this.idStore
                const channelId = this.channelId
                const productInChannel = await channelLookupQueue.run(
                    channelLookupKey(idStore, channelId, id),
                    (cancelToken) => productService.getAssociableProductInChannelByIdExternalChannel(idStore, channelId, id, {cancelToken}),
                    {force},
                )

                if (this.gradeMode) {
                    // Na grade o certo é o id do pai apontar para o produto com variações
                    const state = resolveGridParent(productInChannel, id)
                    const variationIds = ((productInChannel && productInChannel.variations) || []).map((v) => String(v.idExternalChannel))

                    return {state, match: {name: productInChannel && productInChannel.name, variationIds}, message: ''}
                }

                const match = resolveChannelProduct(productInChannel, id)

                return {state: STATE_BY_MATCH[match.status], match, message: ''}
            } catch (err) {
                // Saiu da página no meio da consulta: não há o que mostrar
                if (isLookupCancelled(err)) {
                    return null
                }

                if (isNotFoundError(err)) {
                    return {state: 'notFound', match: null, message: ''}
                }

                return {state: 'error', match: null, message: (err && err.message) || '', timedOut: isLookupTimeout(err)}
            }
        },
        async checkCurrent({force = false} = {}) {
            if (!this.hasId) {
                this.current = {state: 'empty', match: null, message: ''}
                return
            }

            // Canal cujo agente não consulta por id: diria "não encontrado" para qualquer id
            if (!this.searchProfile.supportsGetById) {
                this.current = {state: 'unsupported', match: null, message: ''}
                return
            }

            const id = this.storeChannel.idExternalChannel
            this.current = {state: 'loading', match: null, message: ''}

            this.stopObserving()
            const result = await this.lookup(id, force)

            // O id pode ter mudado enquanto o canal respondia
            if (!result || this.destroyed || id !== this.storeChannel.idExternalChannel) {
                return
            }

            if (this.gradeMode && (result.state === 'gridOk' || result.state === 'gridSimple')) {
                this.parentResult = {...result, parentId: id}
                await this.verifyGrid(result, id, force)
                return
            }

            this.current = result
        },
        /**
         * Confere a grade contra o canal. O que veio no pai já vale; o que não veio (canal que
         * não lista as variações pelo pai, ou id em outro formato) é conferido pelo próprio id,
         * em fila. Sozinho, confere no máximo GRID_AUTO_CHECK variações; o resto fica para o
         * botão "Conferir as outras", para uma grade de 100+ não virar 100+ consultas ao abrir.
         */
        async verifyGrid(parentResult, parentId, force = false) {
            const run = (this.gridRun || 0) + 1
            this.gridRun = run

            const fromParent = parentResult.match.variationIds.map(String)
            const ids = [...new Set(this.gridVariations.map((v) => v.currentId).filter((id) => !!id).map(String))]
            const pending = ids.filter((id) => !fromParent.includes(id))
            const auto = pending.slice(0, GRID_AUTO_CHECK)

            this.gridCheck = {
                ...emptyGridCheck(),
                found: ids.filter((id) => fromParent.includes(id)),
                checking: auto,
                unchecked: pending.slice(GRID_AUTO_CHECK),
            }
            this.gridParent = {result: parentResult, parentId, fromParent}

            if (auto.length) {
                this.current = {state: parentResult.state === 'gridSimple' ? 'gridChecking' : 'gridOk', match: parentResult.match, message: ''}
            } else {
                this.current = parentResult
            }

            await this.checkVariationIds(auto, run, force)
        },
        // Botão "Conferir as outras N variações"
        async verifyRemaining() {
            const ids = this.gridCheck.unchecked

            if (!ids.length || !this.gridParent || this.gridCheck.checking.length) {
                return
            }

            this.gridCheck.unchecked = []
            this.gridCheck.checking = [...ids]

            await this.checkVariationIds(ids, this.gridRun, true)
        },
        async checkVariationIds(ids, run, force) {
            const check = this.gridCheck
            const {result: parentResult, parentId, fromParent} = this.gridParent

            for (const id of ids) {
                const outcome = await this.lookupVariation(id, force)

                if (this.destroyed || run !== this.gridRun || parentId !== this.storeChannel.idExternalChannel) {
                    return
                }

                check.checking = check.checking.filter((c) => c !== id)
                check[outcome].push(id)
                check.oneByOne = check.oneByOne || outcome === 'found'
            }

            // Pai "simples" para o canal, mas as variações existem: a grade confere
            if (parentResult.state === 'gridSimple' && check.found.length) {
                this.current = {state: 'gridOk', match: parentResult.match, message: ''}
            } else if (ids.length && this.current.state === 'gridChecking') {
                this.current = parentResult
            }

            if (this.current.state === 'gridOk') {
                this.$emit('checked', {variationIds: [...new Set([...fromParent, ...check.found])]})
            }
        },
        // 'found' | 'missing' | 'failed'
        async lookupVariation(id, force) {
            try {
                const idStore = this.idStore
                const channelId = this.channelId
                const product = await channelLookupQueue.run(
                    channelLookupKey(idStore, channelId, id),
                    (cancelToken) => productService.getAssociableProductInChannelByIdExternalChannel(idStore, channelId, id, {cancelToken}),
                    {force},
                )

                return resolveChannelProduct(product, id).status === ChannelProductMatchStatus.Found ? 'found' : 'missing'
            } catch (err) {
                return isNotFoundError(err) ? 'missing' : 'failed'
            }
        },

    },
}
</script>

<style lang="scss" scoped>
$display: 'Bricolage Grotesque', 'Source Sans Pro', system-ui, sans-serif;
$mono: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
$ease-out: cubic-bezier(.22, 1, .36, 1);

.pcl-card {
    // Cor do estado: pinta o fio, os nós e o veredito
    --pcl-tone: var(--border-strong);
    --pcl-glow: var(--brand);

    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid var(--border-subtle);
    border-radius: 16px;
    background: var(--surface-1);
    box-shadow: var(--shadow-sm);

    // Luz da marca no canto, discreta
    &::before {
        content: '';
        position: absolute;
        top: -70px;
        left: -50px;
        width: 200px;
        height: 150px;
        background: radial-gradient(closest-side, color-mix(in srgb, var(--pcl-glow) 16%, transparent), transparent);
        pointer-events: none;
    }

    &.is-ok { --pcl-tone: var(--success-solid); }
    &.is-warn { --pcl-tone: var(--warning-solid); }
    &.is-bad { --pcl-tone: var(--danger-solid); }
}

.pcl-head {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 18px 14px;
}

.pcl-head-text {
    flex: 1;
    min-width: 0;
}

.pcl-title {
    display: -webkit-box;
    margin: 0 0 5px;
    overflow: hidden;
    color: var(--text-strong);
    font-family: $display;
    font-size: 17px;
    font-weight: 800;
    letter-spacing: -.3px;
    line-height: 1.2;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
}

.pcl-status {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: 6px;
    padding: 2px 10px 2px 8px;
    border: 1px solid;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;

    &.is-active {
        color: var(--brand);
        background: var(--tint-brand-bg);
        border-color: var(--tint-brand-border);
    }

    &.is-inactive {
        color: var(--warning-fg);
        background: var(--warning-bg);
        border-color: var(--warning-border);
    }

    &.is-off {
        color: var(--text-muted);
        background: var(--surface-3);
        border-color: var(--border);
    }
}

.pcl-status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
}

.pcl-link {
    position: relative;
    display: flex;
    flex: 1;
    gap: 12px;
    padding: 4px 18px 16px;
}

.pcl-wire {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 10px;
    padding-top: 24px;
}

.pcl-node {
    flex-shrink: 0;
    width: 10px;
    height: 10px;
    border: 2px solid var(--pcl-tone);
    border-radius: 50%;
    background: var(--surface-1);
    transition: border-color .3s, background-color .3s;
}

.pcl-node-bottom {
    background: var(--pcl-tone);
}

.pcl-wire-line {
    flex: 1;
    width: 2px;
    min-height: 18px;
    margin: 3px 0;
    border-radius: 2px;
    background: repeating-linear-gradient(to bottom, var(--pcl-tone) 0 4px, transparent 4px 7px);
    transition: background .3s;

    .is-ok & {
        background: var(--pcl-tone);
    }
}

.pcl-link-body {
    flex: 1;
    min-width: 0;
}

.pcl-label {
    margin: 0 0 4px;
    color: var(--text-faint);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .12em;
    text-transform: uppercase;
}

.pcl-id-row {
    display: flex;
    align-items: center;
    gap: 6px;
    min-height: 34px;
}

.pcl-id {
    padding: 4px 10px;
    border: 1px solid var(--border-subtle);
    border-radius: 8px;
    color: var(--text-strong);
    background: var(--surface-2);
    font-family: $mono;
    font-size: 14px;
    font-weight: 600;
    word-break: break-all;

    &.is-empty {
        color: var(--text-muted);
        font-family: inherit;
        font-weight: 600;
    }
}

.pcl-swap-btn {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: 6px;
    height: 30px;
    padding: 0 10px;
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--text-muted);
    background: var(--surface-1);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: color .15s, background-color .15s, border-color .15s;

    svg {
        font-size: 11px;
    }

    &:hover,
    &:focus-visible {
        color: var(--brand);
        background: var(--tint-brand-bg);
        border-color: var(--tint-brand-border);
        text-decoration: none;
        outline: none;
    }
}

// Ação principal quando a conferência acusa problema: chama atenção sem gritar
.pcl-fix-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    margin-top: 10px;
    padding: 7px 12px;
    border: 1px solid var(--brand-solid);
    border-radius: 9px;
    color: var(--text-on-brand);
    background: var(--brand-solid);
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: background-color .15s, transform .15s $ease-out;

    svg {
        font-size: 11px;
    }

    &:hover {
        background: var(--brand-fill);
    }

    &:active {
        transform: translateY(1px);
    }

    &:focus-visible {
        box-shadow: 0 0 0 3px var(--focus-ring);
        outline: none;
    }
}




.pcl-result {
    min-height: 8px;
    margin-top: 12px;
}

.pcl-verdict {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0 0 4px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: .02em;
    animation: pcl-rise .35s $ease-out both;

    &.is-ok { color: var(--success-fg); }
    &.is-warn { color: var(--warning-fg); }
    &.is-bad { color: var(--danger-fg); }
    &.is-muted { color: var(--text-muted); }
}

.pcl-product-name {
    margin: 0;
    color: var(--text-strong);
    font-size: 15px;
    font-weight: 700;
    line-height: 1.3;
    animation: pcl-rise .35s .04s $ease-out both;
}

.pcl-product-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 10px;
    margin: 6px 0 0;
    color: var(--text-muted);
    font-size: 12px;
    animation: pcl-rise .35s .08s $ease-out both;
}

.pcl-kind {
    padding: 1px 8px;
    border-radius: 999px;
    color: var(--text-soft);
    background: var(--surface-3);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;

    &.is-child {
        color: var(--brand);
        background: var(--tint-brand-bg);
    }
}

.pcl-product-ids {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    margin: 6px 0 0;
    color: var(--text-faint);
    font-size: 12px;

    code {
        color: var(--text-muted);
        font-family: $mono;
    }
}

.pcl-hint {
    margin: 4px 0 0;
    color: var(--text-muted);
    font-size: 13px;
    line-height: 1.45;

    code {
        color: var(--text-strong);
        font-family: $mono;
    }
}

.pcl-link-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    padding: 0;
    border: 0;
    color: var(--brand);
    background: none;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
}

// Não pode alargar o card: o texto encolhe com reticências
.pcl-grid-more {
    max-width: 100%;
    min-width: 0;

    span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.pcl-skeleton {
    display: grid;
    gap: 8px;
    padding-top: 2px;

    span {
        height: 12px;
        border-radius: 6px;
        background: linear-gradient(90deg, var(--surface-3) 0%, var(--surface-2) 50%, var(--surface-3) 100%);
        background-size: 200% 100%;
        animation: pcl-shimmer 1.2s linear infinite;

        &:first-child { width: 45%; }
        &:last-child { width: 85%; height: 16px; }
    }
}









.pcl-figures {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
    margin: 0;
    border-top: 1px solid var(--border-subtle);
    background: var(--surface-2);

    div {
        padding: 10px 12px 12px 18px;

        & + div {
            border-left: 1px solid var(--border-subtle);
            padding-left: 12px;
        }
    }

    dt {
        color: var(--text-faint);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: .12em;
        text-transform: uppercase;
    }

    dd {
        margin: 2px 0 0;
        color: var(--text-strong);
        font-size: 14px;
        font-variant-numeric: tabular-nums;
        font-weight: 700;
        white-space: nowrap;
    }
}

@keyframes pcl-rise {
    from {
        opacity: 0;
        transform: translateY(4px);
    }
}

@keyframes pcl-shimmer {
    to {
        background-position: -200% 0;
    }
}

@media (prefers-reduced-motion: reduce) {
    .pcl-verdict,
    .pcl-product-name,
    .pcl-product-meta,
    .pcl-skeleton span {
        animation: none;
    }
}
</style>
